/**
 * useApiMutation — React Query wrapper with Api injection
 *
 * Wraps @tanstack/react-query's useMutation so that the mutationFn receives
 * the Api instance from context automatically.
 *
 * Pattern adapted from influenster-v4-mobile/src/api/hooks/useMutation.ts
 *
 * Usage:
 *   export function useClaimCampaign() {
 *     return useApiMutation({
 *       mutationFn: (api) => async ({ campaignId, request }) => {
 *         await api.campaigns.campaignsControllerClaimCampaign(campaignId, request);
 *       },
 *     });
 *   }
 */

import {
  type DefaultError,
  type MutationFunction,
  useMutation as useMutationBase,
  type UseMutationOptions,
  type UseMutationResult,
} from '@tanstack/react-query';
import { useMemo } from 'react';
import { useApi } from '@/contexts/ApiContext';
import type { Api } from '@/api/Api';

export type ApiMutationFnResolver<TData, TVariables> = (
  api: Api,
) => MutationFunction<TData, TVariables>;

interface UseApiMutationOptions<
  TData = unknown,
  TError = DefaultError,
  TVariables = void,
  TContext = unknown,
> extends Omit<UseMutationOptions<TData, TError, TVariables, TContext>, 'mutationFn'> {
  mutationFn?: ApiMutationFnResolver<TData, TVariables>;
}

type UseApiMutation = <
  TData = unknown,
  TError = DefaultError,
  TVariables = void,
  TContext = unknown,
>(
  options: UseApiMutationOptions<TData, TError, TVariables, TContext>,
) => UseMutationResult<TData, TError, TVariables, TContext>;

const useApiMutation: UseApiMutation = (apiMutationOptions) => {
  const api = useApi();
  const { mutationFn: mutationFnResolver, ...options } = apiMutationOptions;

  const mutationFn = useMemo(
    () => (mutationFnResolver ? mutationFnResolver(api) : undefined),
    [api, mutationFnResolver],
  );

  return useMutationBase({ ...options, mutationFn });
};

export default useApiMutation;
export { useApiMutation };
